import React from 'react';
import { Box, Typography, IconButton, List, ListItemIcon, ListItemText, Switch, ListItem, Button } from '@mui/material';
import { ReactComponent as AddIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as RemoveIcon } from "../../../assets/icons/minus.svg";
import Checkbox from '../../../components/Checkbox';
import { ReactComponent as AddCircleWhite } from "../../../assets/icons/add-circle-white.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/search.svg";
import ImageSearchModal from '../../../components/ImageSearchModal';
import { getBrandFilterOptions, DIRECT_BRANDS_EXCLUDE } from '../../../constants/brandFilter';


const CheckboxItem = ({ item, isChecked = false, onChange }) => {
    const [checked, setChecked] = React.useState(isChecked);

    const handleCheck = () => {
        setChecked((prev) => !prev);
        onChange(item.value, !checked);
    };

    return (
        <ListItem disablePadding>
            <ListItemIcon>
                <Checkbox
                    onChange={handleCheck}
                    sx={{
                        width: 32,
                        height: 32,
                        color: '#D0D5DD',
                        '&.Mui-checked': { color: '#28282A' },
                    }}
                    checked={checked}
                    disableRipple
                />
            </ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2', color: 'textSecondary' }} />
        </ListItem>
    );
};

const CategorySection = ({ title, items, isExpanded = false, onSelect }) => {
    const [expanded, setExpanded] = React.useState(isExpanded);

    const toggleExpand = () => setExpanded((prev) => !prev);

    return (
        <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body1">{title}</Typography>
                {items.length > 0 ? (
                    <IconButton onClick={toggleExpand}>
                        {expanded ? <RemoveIcon className='text-[20px]' /> : <AddIcon className='text-[20px]' />}
                    </IconButton>
                ) : (
                    null
                )}
            </Box>
            {expanded && items.length > 0 && (
                <List disablePadding>
                    {items.map((item, index) => (
                        <CheckboxItem key={`${title}-${index}`} item={item} onChange={onSelect} />
                    ))}
                </List>
            )}
            {items.length === 0 && (
                <CheckboxItem item={{ label: title, value: title.toLowerCase() }} onChange={onSelect} />
            )}
        </Box>
    );
};

const FilterSidebar = ({ setSearchState, setOpenProductModal }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [openImageSearchModal, setOpenImageSearchModal] = React.useState(false);

    const brands = [getBrandFilterOptions(expanded)];

    const categories = [
        {
            label: 'Category',
            items: [
                { label: 'Furniture', value: 'furniture' },
                { label: 'Lighting & Fans', value: 'lighting fans' },
                { label: 'Kitchen & Dining', value: 'kitchen dining' },
                { label: 'Rugs', value: 'rugs' },
                { label: 'Outdoor & Garden', value: 'outdoor garden' },
                { label: 'Kids & Nursery', value: 'kids nursery' },
                { label: 'Organization', value: 'organization' },
                { label: 'Fixtures', value: 'fixtures' },
                { label: 'Building Materials', value: 'building materials' },
                { label: 'Window Treatments', value: 'window treatments' },
                { label: 'Seasonal & Specialty', value: 'seasonal specialty' },
                { label: 'Other', value: 'other' }
            ]
        },
    ];

    const handleSelect = (value, isChecked) => {
        setSearchState((prevState) => {
            const updatedSearch = isChecked
                ? prevState.search ? `${prevState.search},${value}` : value
                : prevState.search.split(',').filter(item => item !== value).join(',');
            return { ...prevState, search: updatedSearch };
        });
    };

    const handleBrandSelect = (value, isChecked) => {
        setSearchState((prevState) => {
            const fromBrand = isChecked
                ? prevState.from ? `${prevState.from},${value}` : value
                : prevState.from.split(',').filter(item => item !== value).join(',');
            return { ...prevState, from: fromBrand };
        });
    };

    const handleDirectBrandFilter = () => {
        setExpanded((prevExpanded) => {
            const nextExpanded = !prevExpanded;

            setSearchState((prevState) => {
                return {
                    ...prevState,
                    excludeFrom: nextExpanded ? DIRECT_BRANDS_EXCLUDE.join(',') : "",
                };
            });

            return nextExpanded;
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 220, minWidth: 220 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Direct Brands Only
                </Typography>
                <Switch checked={expanded} onChange={handleDirectBrandFilter} color='black' />
            </Box>
            <Box sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {brands.map((category, index) => (
                    <CategorySection key={index} title={category.label} items={category.items} onSelect={handleBrandSelect} />
                ))}
            </Box>

            {categories?.length > 0 && (
                categories.map((category, index) => (
                    <CategorySection key={index} title={category.label} items={category.items} onSelect={handleSelect} />
                ))
            )}
            <Box sx={{ my: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Add Product Manually
                </Typography>
                <Button onClick={() => setOpenProductModal(true)}>
                    <AddCircleWhite />
                    <Typography sx={{ ml: 1 }}>
                        Add Product
                    </Typography>
                </Button>
            </Box>

            <Box sx={{ my: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Search by Image
                </Typography>
                <Button onClick={() => setOpenImageSearchModal(true)}>
                    <SearchIcon />
                    <Typography sx={{ ml: 1 }}>
                        Mood Board Search
                    </Typography>
                </Button>
            </Box>

            <ImageSearchModal 
                open={openImageSearchModal} 
                openSet={setOpenImageSearchModal} 
            />

        </Box>
    );
};

export default FilterSidebar;